import React, { useEffect, useState, useCallback } from 'react';
import { timeAgo, fmtDate, fmtDateTime, parseUTC } from '../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { godAuditApi } from '../../api';
import { GS, G } from './_godTheme';

const ACTION_META = {
  GOD_LOGIN:                  { icon: '🔑', color: '#6366F1', label: 'God Login'           },
  GOD_LOGOUT:                 { icon: '🚪', color: '#6B7280', label: 'God Logout'          },
  COMPANY_CREATED:            { icon: '🏢', color: '#16A34A', label: 'Company Created'     },
  COMPANY_UPDATED:            { icon: '✏️', color: '#D97706', label: 'Company Updated'     },
  COMPANY_SUSPENDED:          { icon: '🚫', color: '#DC2626', label: 'Suspended'           },
  COMPANY_REACTIVATED:        { icon: '✅', color: '#16A34A', label: 'Reactivated'         },
  SUPER_ADMIN_PASSWORD_RESET: { icon: '🔐', color: '#D97706', label: 'SA Password Reset'  },
  COMPANY_DELETED:            { icon: '🗑',  color: '#DC2626', label: 'Company Deleted'    },
  DATA_EXPORTED:              { icon: '📦', color: '#2563EB', label: 'Data Exported'       },
};

export default function GodAuditLog() {
  const navigate             = useNavigate();
  const [data,    setData]   = useState({ items: [], total: 0, totalPages: 1 });
  const [page,    setPage]   = useState(0);
  const [loading, setLoading]= useState(true);
  const [search,  setSearch] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    godAuditApi.getAll({ page, size: 30 })
      .then(r => setData(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [search]);

  const filtered = data.items.filter(log =>
    !search ||
    log.action?.toLowerCase().includes(search.toLowerCase()) ||
    log.performedBy?.toLowerCase().includes(search.toLowerCase()) ||
    log.targetCompanyName?.toLowerCase().includes(search.toLowerCase()) ||
    log.details?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ maxWidth: '960px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={GS.title}>Audit Log</h1>
        <p style={GS.sub}>{data.total} entr{data.total !== 1 ? 'ies' : 'y'} — append-only, permanent record</p>
      </div>

      {/* Info banner */}
      <div style={{ padding: '12px 16px', background: G.goldLight, border: `1px solid ${G.goldBorder}`, borderRadius: '12px', marginBottom: '16px', fontSize: '13px', color: G.goldDeep }}>
        <strong>Every God Mode action is logged here permanently.</strong> This log cannot be edited or deleted.
        Use it to audit what changes were made, when, and by whom.
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: '16px' }}>
        <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: G.textFaint, fontSize: '14px', pointerEvents: 'none' }}>🔍</span>
        <input placeholder="Search by action, user, company, or details…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...GS.input, paddingLeft: '36px' }}
          onFocus={e => e.target.style.borderColor = G.gold} onBlur={e => e.target.style.borderColor = '#E5E7EB'} />
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {[1,2,3,4,5].map(i => <div key={i} className="skeleton" style={{ height: '60px', borderRadius: '10px' }} />)}
        </div>
      ) : !filtered.length ? (
        <div style={{ textAlign: 'center', padding: '60px', color: G.textMuted }}>
          <p style={{ fontSize: '32px', marginBottom: '12px' }}>📋</p>
          <p style={{ fontWeight: 600, color: G.textSub }}>{search ? 'No entries match' : 'No audit entries yet'}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {filtered.map(log => {
            const meta = ACTION_META[log.action] || { icon: '•', color: G.gold, label: log.action?.replace(/_/g, ' ') };
            return (
              <div key={log.id} style={{
                display: 'flex', alignItems: 'flex-start', gap: '12px',
                padding: '12px 14px', background: '#fff', borderRadius: '10px',
                border: '1px solid #F1F5F9',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              }}>
                <span style={{ fontSize: '18px', flexShrink: 0, marginTop: '1px' }}>{meta.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '3px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px', background: `${meta.color}18`, color: meta.color, border: `1px solid ${meta.color}33` }}>
                      {meta.label}
                    </span>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: G.text }}>{log.performedBy}</span>
                    {log.targetCompanyName && (
                      <span onClick={() => log.targetCompanyId && navigate(`/god/companies/${log.targetCompanyId}`)}
                        style={{ fontSize: '12px', color: '#2563EB', cursor: log.targetCompanyId ? 'pointer' : 'default', fontWeight: 500 }}>
                        → {log.targetCompanyName}
                      </span>
                    )}
                  </div>
                  {log.details && (
                    <p style={{ fontSize: '12px', color: G.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.details}
                    </p>
                  )}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <p style={{ fontSize: '11px', color: G.textMuted, fontWeight: 500 }}>{timeAgo(log.timestamp)}</p>
                  <p style={{ fontSize: '10px', color: G.textFaint, marginTop: '2px' }}>
                    {fmtDateTime(log.timestamp)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data.totalPages > 1 && (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '20px', alignItems: 'center' }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
            style={{ padding: '7px 16px', borderRadius: '8px', cursor: page === 0 ? 'not-allowed' : 'pointer', fontSize: '13px', background: '#fff', color: page === 0 ? G.textFaint : G.textSub, border: '1px solid #E5E7EB', opacity: page === 0 ? 0.5 : 1 }}>
            ← Prev
          </button>
          <span style={{ fontSize: '13px', color: G.textMuted }}>Page {page + 1} of {data.totalPages}</span>
          <button onClick={() => setPage(p => Math.min(data.totalPages - 1, p + 1))} disabled={page >= data.totalPages - 1}
            style={{ padding: '7px 16px', borderRadius: '8px', cursor: page >= data.totalPages - 1 ? 'not-allowed' : 'pointer', fontSize: '13px', background: '#fff', color: page >= data.totalPages - 1 ? G.textFaint : G.textSub, border: '1px solid #E5E7EB', opacity: page >= data.totalPages - 1 ? 0.5 : 1 }}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
